import React, { useState, useCallback, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { getCaseStudyBySlug, CaseStudyRow } from "@/lib/api";
import "@/styles/pages.css";
import "@/components/Floaters/floaters.css";

const ytId = (url?: string | null): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const CaseStudyDetail: React.FC = () => {
  useReveal();
  const { slug } = useParams();
  const [study, setStudy] = useState<CaseStudyRow | null | undefined>(undefined);
  const [lb, setLb] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!slug) { setStudy(null); return; }
    getCaseStudyBySlug(slug).then(row => { if (!cancelled) setStudy(row); }).catch(() => { if (!cancelled) setStudy(null); });
    return () => { cancelled = true; };
  }, [slug]);

  const photos = study?.photos || [];
  const close = useCallback(() => setLb(null), []);
  const next = useCallback(() => setLb(i => i === null ? 0 : (i + 1) % (photos.length || 1)), [photos.length]);
  const prev = useCallback(() => setLb(i => i === null ? 0 : (i - 1 + (photos.length || 1)) % (photos.length || 1)), [photos.length]);

  useEffect(() => {
    if (lb === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lb, close, next, prev]);

  if (study === undefined) return <main className="iav-page" style={{ minHeight: "100vh" }} data-testid="case-study-detail-loading" />;
  if (study === null) return <Navigate to="/case-studies" replace />;
  const yid = ytId(study.youtube_url);
  const overview: [string, string | null | undefined][] = [
    ["Client", study.client],
    ["Date", study.event_date],
    ["Venue", study.venue],
    ["Location", study.location],
    ["Audience", study.audience_size],
  ];
  const filledOverview = overview.filter(([, v]) => v && String(v).trim().length > 0);

  return (
    <main className="iav-page" data-testid="case-study-detail">
      <section className="iav-pd-hero" data-testid="case-study-hero">
        {study.hero_image_url && <img src={study.hero_image_url} alt={study.title} />}
        <div className="iav-pd-meta">
          <span className="iav-pd-cat" data-reveal>{[study.client, study.category].filter(Boolean).join(" · ")}</span>
          <h1 className="iav-pd-title" data-reveal>{study.title}</h1>
        </div>
      </section>

      <div className="iav-pd-body">
        {filledOverview.length > 0 && (<>
          <h2 className="iav-cs-h" data-reveal>Overview</h2>
          <div className="iav-cs-overview-grid" data-reveal>
            {filledOverview.map(([k, v]) => (
              <div key={k} className="iav-cs-overview-item">
                <span className="iav-cs-overview-lbl">{k}</span>
                <span className="iav-cs-overview-val">{v}</span>
              </div>
            ))}
          </div>
        </>)}
        {study.challenge && (<>
          <h2 className="iav-cs-h" data-reveal>The Challenge</h2>
          <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: study.challenge }} />
        </>)}
        {study.solution && (<>
          <h2 className="iav-cs-h" data-reveal>The Solution</h2>
          <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: study.solution }} />
        </>)}
        {study.execution && (<>
          <h2 className="iav-cs-h" data-reveal>Execution</h2>
          <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: study.execution }} />
        </>)}
        {study.technical_specs && (<>
          <h2 className="iav-cs-h" data-reveal>Technical Specs</h2>
          <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: study.technical_specs }} />
        </>)}
        {study.result && (<>
          <h2 className="iav-cs-h" data-reveal>The Result</h2>
          <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: study.result }} />
        </>)}
        {study.team_credits && (<>
          <h2 className="iav-cs-h" data-reveal>Team &amp; Credits</h2>
          <div className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: study.team_credits }} />
        </>)}
      </div>

      {study.stats && study.stats.length > 0 && (
        <section className="iav-cs-stats" data-testid="case-study-stats">
          {study.stats.map((s, i) => (
            <div key={i} className="iav-cs-stat">
              <div className="iav-stat-num">{s.value}</div>
              <div className="iav-stat-label">{s.label}</div>
            </div>
          ))}
        </section>
      )}

      {photos.length > 0 && (
        <div className="iav-pd-photos" data-testid="case-study-photos">
          {photos.map((p, i) => (
            <button key={i} className="iav-pd-photo" onClick={() => setLb(i)} aria-label={`Open photo ${i + 1}`} data-cursor="cta">
              <img src={p} alt={`${study.title} photo ${i + 1}`} loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {study.testimonial_quote && (
        <section className="iav-cs-quote" data-testid="case-study-quote">
          <blockquote className="iav-rich" data-reveal dangerouslySetInnerHTML={{ __html: study.testimonial_quote }} />
          {study.testimonial_author && <cite className="iav-cs-quote-author" data-reveal>— {study.testimonial_author}</cite>}
        </section>
      )}

      {yid && (
        <section className="iav-pd-yt">
          <h2>Watch the Show</h2>
          <iframe className="iav-pd-yt-frame" src={`https://www.youtube.com/embed/${yid}`} title={`${study.title} video`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </section>
      )}

      <div className="iav-pd-cta">
        <Link to="/quote" className="iav-btn iav-btn-primary">Get a Quote</Link>
        <Link to="/case-studies" className="iav-btn iav-btn-outline">Back to Case Studies</Link>
      </div>

      {lb !== null && photos.length > 0 && (
        <div className="iav-lb" onClick={close} role="dialog">
          <button className="iav-lb-close" onClick={(e) => { e.stopPropagation(); close(); }} aria-label="Close">✕</button>
          <button className="iav-lb-arrow iav-lb-prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous">‹</button>
          <button className="iav-lb-arrow iav-lb-next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next">›</button>
          <img className="iav-lb-img" src={photos[lb]} alt={`Photo ${lb + 1}`} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </main>
  );
};
export default CaseStudyDetail;
